// TRIL-116: settings page section per spec §360. Always-available form
// + permanent opt-out toggle. Opt-out suppresses all nudge surfaces but
// the section itself stays accessible.

import * as React from 'react';
import { trackEvent } from '../../../helpers/analytics';
import type {
  ProfessionalContext,
  ProfessionalContextInput,
} from '../../../interfaces/user.interface';
import { ProfessionalContextForm } from '../../PlanC/ProfessionalContextForm';
import './professional-background-view.scss';

interface Props {
  context: ProfessionalContext | null;
  optedOut: boolean;
  onSubmit: (input: ProfessionalContextInput) => Promise<void>;
  onOptOut: () => Promise<void>;
  onReEnable?: () => Promise<void>;
}

export function ProfessionalBackgroundView({
  context,
  optedOut,
  onSubmit,
  onOptOut,
  onReEnable,
}: Props): React.ReactElement {
  const handleOptOut = (): void => {
    trackEvent('plan_c.opt_out', {});
    void onOptOut();
  };

  return (
    <section className="prof-bg-view">
      <header className="prof-bg-view__header">
        <h2>Professional Background</h2>
        <p>
          Help URG rate your expertise more accurately by sharing your
          background. Used only by the reputation scoring system; never shared
          with other users.
        </p>
      </header>

      {optedOut ? (
        <div className="prof-bg-view__opted-out">
          <p>You've opted out of profile-completion nudges.</p>
          <button type="button" onClick={() => void onReEnable?.()}>
            Re-enable nudges
          </button>
        </div>
      ) : (
        <>
          <ProfessionalContextForm
            initialValue={context ?? undefined}
            onSubmit={onSubmit}
            onCancel={() => {
              /* no-op: settings page form has no cancel target */
            }}
            entrySurface="settings"
          />
          {/* TRIL-146: only surface the opt-out CTA when the user has not
              yet filled out their professional context. Once context is
              saved (read mode), the nudge surfaces won't fire anyway —
              showing "Don't show me this again" there is dead UX and is
              actively confusing (the user just FILLED IT OUT). */}
          {!context && (
            <button
              type="button"
              onClick={handleOptOut}
              className="prof-bg-view__opt-out"
            >
              Don't show me this again
            </button>
          )}
        </>
      )}
    </section>
  );
}
